import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import Swal from 'sweetalert2';
import styles from "./ParticipantInfoForm.module.css";
import { submitParticipantInfo, downloadParticipantPdfDirect } from '../../../service/register.service';
import { format } from 'date-fns';

const validationSchema = Yup.object({
    fullName: Yup.string().required('Full Name is required'),
    designation: Yup.string().required('Designation / Role is required'),
    organization: Yup.string().required('Organization name is required'),
    nationality: Yup.string().required('Nationality is required'),
    passportNumber: Yup.string().required('Passport number is required'),
    dateOfIssue: Yup.date().required('Date of issue is required').nullable(),
    dateOfExpiry: Yup.date()
        .required('Date of expiry is required')
        .nullable()
        .min(Yup.ref('dateOfIssue'), 'Expiry date must be after issue date'),
    placeOfIssue: Yup.string().required('Place of issue is required'),
    countryOfBirth: Yup.string().required('Country of birth is required'),
    emailId: Yup.string().email('Invalid email address').required('Email ID is required'),
    mobileNumber: Yup.string()
        .matches(/^[0-9+\-\s()]*$/, 'Invalid mobile number')
        .required('Mobile number is required'),
    eventDetails: Yup.string().required('Event details are required'),
});

const ParticipantInfoForm = () => {
    const initialValues = {
        fullName: '',
        designation: '',
        organization: '',
        nationality: '',
        passportNumber: '',
        dateOfIssue: null,
        dateOfExpiry: null,
        placeOfIssue: '',
        countryOfBirth: '',
        emailId: '',
        mobileNumber: '',
        eventDetails: 'International Vacuum Electronics Conference (IVEC’27)',
    };

    const handleSubmit = async (values, { resetForm, setSubmitting, setFieldError }) => {
        try {
            const payload = {
                fullName: values.fullName.trim(),
                designation: values.designation.trim(),
                organization: values.organization.trim(),
                nationality: values.nationality.trim(),
                passportNumber: values.passportNumber.trim(),
                dateOfIssue: format(new Date(values.dateOfIssue), "yyyy-MM-dd"),
                dateOfExpiry: format(new Date(values.dateOfExpiry), "yyyy-MM-dd"),
                placeOfIssue: values.placeOfIssue.trim(),
                countryOfBirth: values.countryOfBirth.trim(),
                emailId: values.emailId.trim().toLowerCase(),
                mobileNumber: values.mobileNumber.trim(),
                eventDetails: values.eventDetails ? values.eventDetails.trim() : "",
            };

            const response = await submitParticipantInfo(payload);

            if (!response?.success) {
                await Swal.fire({
                    title: "Submission Failed",
                    text: response?.message || "Unable to submit participant information.",
                    icon: "error",
                    confirmButtonText: "OK",
                });
                return;
            }

            const savedData = response.data || payload;

            // Trigger immediate download of the exact official server-generated PDF
            if (response.data?.id) {
                try {
                    await downloadParticipantPdfDirect(response.data.id, response.data.fullName);
                } catch (pdfErr) {
                    console.warn("Direct PDF download note:", pdfErr);
                }
            }

            await Swal.fire({
                title: "Clearance Form Submitted!",
                html: `
                    <div style="text-align: left; font-size: 0.95rem; color: #334155; line-height: 1.6;">
                        <p style="margin: 0 0 12px 0;">
                            Dear <strong>${savedData.fullName}</strong>, your participant information for IVEC 2027 political clearance has been successfully submitted.
                        </p>
                        <div style="background: #f0fdf4; border: 1px solid #86efac; border-radius: 8px; padding: 14px; margin-bottom: 12px;">
                            <p style="margin: 0 0 4px 0; color: #166534; font-weight: 700; font-size: 0.9rem;">
                                ✉️ Email Dispatched with Attached PDF:
                            </p>
                            <p style="margin: 0; color: #15803d; font-size: 0.85rem;">
                                An official copy of this clearance document has been sent to <strong>${savedData.emailId}</strong>.
                            </p>
                        </div>
                        <p style="margin: 0; color: #64748b; font-size: 0.82rem;">
                            📥 A PDF copy has also been automatically downloaded to your device for your visa records.
                        </p>
                    </div>
                `,
                icon: "success",
                confirmButtonColor: "#2b53a3",
                confirmButtonText: "Done",
            });

            resetForm();
        } catch (error) {
            const status = error.response?.status;
            const data = error.response?.data;
            const message = data?.message || "Unable to submit participant details. Please try again.";

            // Standard duplicate handling (HTTP 409 Conflict)
            if (status === 409) {
                const lowerMsg = message.toLowerCase();
                if (lowerMsg.includes("email")) {
                    setFieldError("emailId", message);
                } else if (lowerMsg.includes("mobile")) {
                    setFieldError("mobileNumber", message);
                } else if (lowerMsg.includes("passport")) {
                    setFieldError("passportNumber", message);
                }

                await Swal.fire({
                    title: "Duplicate Submission Detected",
                    html: `
                        <div style="text-align: left; font-size: 0.92rem; color: #334155;">
                            <p style="margin-bottom: 8px;">${message}</p>
                            <p style="color: #64748b; font-size: 0.82rem; margin: 0;">
                                If you have already submitted this form and need to update your details or retrieve your PDF clearance dossier, please contact the conference secretariat.
                            </p>
                        </div>
                    `,
                    icon: "warning",
                    confirmButtonColor: "#2b53a3",
                    confirmButtonText: "OK",
                });
                return;
            }

            if (status === 400 && data?.data && typeof data.data === "object") {
                Object.entries(data.data).forEach(([field, msg]) => {
                    setFieldError(field, String(msg));
                });
            }

            await Swal.fire({
                title: "Submission Error",
                text: message,
                icon: "error",
                confirmButtonColor: "#2b53a3",
                confirmButtonText: "OK",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.card}>
                <div className={styles.header}>
                    <div className={styles.badgeWrapper}>
                        <span className={styles.badge}>IVEC-2027 Clearance Form</span>
                    </div>
                    <h2>Participant Information</h2>
                    <p>Please complete the details below for political clearance to attend the conference.</p>
                </div>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue, errors, touched, isSubmitting }) => (
                        <Form className='text-start'>
                            {/* Personal Details Section */}
                            <div className={styles.sectionHeader}>
                                <h3>Personal & Academic Details</h3>
                            </div>

                            <div className={styles.gridTwo}>
                                <div className={styles.fieldGroup}>
                                    <label>
                                        Full Name <span className={styles.requiredStar}>*</span> <span className={styles.hint}>(as per passport)</span>
                                    </label>
                                    <Field
                                        type="text"
                                        name="fullName"
                                        placeholder="e.g. Jane Doe"
                                        className={errors.fullName && touched.fullName ? styles.inputError : ''}
                                    />
                                    <ErrorMessage name="fullName" component="span" className={styles.errorText} />
                                </div>

                                <div className={styles.fieldGroup}>
                                    <label>
                                        Designation / Role <span className={styles.requiredStar}>*</span>
                                    </label>
                                    <Field
                                        type="text"
                                        name="designation"
                                        placeholder="e.g. Speaker, Author, Student"
                                        className={errors.designation && touched.designation ? styles.inputError : ''}
                                    />
                                    <ErrorMessage name="designation" component="span" className={styles.errorText} />
                                </div>

                                <div className={styles.fieldGroup}>
                                    <label>
                                        Organization / Institution <span className={styles.requiredStar}>*</span>
                                    </label>
                                    <Field
                                        type="text"
                                        name="organization"
                                        placeholder="e.g. MIT, IEEE"
                                        className={errors.organization && touched.organization ? styles.inputError : ''}
                                    />
                                    <ErrorMessage name="organization" component="span" className={styles.errorText} />
                                </div>

                                <div className={styles.fieldGroup}>
                                    <label>
                                        Nationality / Citizenship <span className={styles.requiredStar}>*</span>
                                    </label>
                                    <Field
                                        type="text"
                                        name="nationality"
                                        placeholder="e.g. American, Indian, German"
                                        className={errors.nationality && touched.nationality ? styles.inputError : ''}
                                    />
                                    <ErrorMessage name="nationality" component="span" className={styles.errorText} />
                                </div>
                            </div>

                            {/* Passport Details Section */}
                            <div className={styles.sectionHeader}>
                                <h3>Passport Information</h3>
                            </div>

                            <div className={styles.gridTwo}>
                                <div className={styles.fieldGroup}>
                                    <label>
                                        Passport Number <span className={styles.requiredStar}>*</span>
                                    </label>
                                    <Field
                                        type="text"
                                        name="passportNumber"
                                        placeholder="e.g. A12345678"
                                        className={errors.passportNumber && touched.passportNumber ? styles.inputError : ''}
                                    />
                                    <ErrorMessage name="passportNumber" component="span" className={styles.errorText} />
                                </div>

                                <div className={styles.fieldGroup}>
                                    <label>
                                        Country of Birth <span className={styles.requiredStar}>*</span>
                                    </label>
                                    <Field
                                        type="text"
                                        name="countryOfBirth"
                                        placeholder="e.g. United States"
                                        className={errors.countryOfBirth && touched.countryOfBirth ? styles.inputError : ''}
                                    />
                                    <ErrorMessage name="countryOfBirth" component="span" className={styles.errorText} />
                                </div>

                                <div className={styles.fieldGroup}>
                                    <label>
                                        Date of Issue <span className={styles.requiredStar}>*</span>
                                    </label>
                                    <DatePicker
                                        selected={values.dateOfIssue}
                                        onChange={(date) => setFieldValue('dateOfIssue', date)}
                                        dateFormat="dd-MM-yyyy"
                                        placeholderText="DD-MM-YYYY"
                                        wrapperClassName={styles.datePickerWrapper}
                                        className={`${styles.customDatePicker} ${errors.dateOfIssue && touched.dateOfIssue ? styles.inputError : ''}`}
                                        maxDate={new Date()}
                                        showYearDropdown
                                        showMonthDropdown
                                        dropdownMode="select"
                                    />
                                    <ErrorMessage name="dateOfIssue" component="span" className={styles.errorText} />
                                </div>

                                <div className={styles.fieldGroup}>
                                    <label>
                                        Date of Expiry <span className={styles.requiredStar}>*</span>
                                    </label>
                                    <DatePicker
                                        selected={values.dateOfExpiry}
                                        onChange={(date) => setFieldValue('dateOfExpiry', date)}
                                        dateFormat="dd-MM-yyyy"
                                        placeholderText="DD-MM-YYYY"
                                        wrapperClassName={styles.datePickerWrapper}
                                        className={`${styles.customDatePicker} ${errors.dateOfExpiry && touched.dateOfExpiry ? styles.inputError : ''}`}
                                        minDate={new Date()}
                                        showYearDropdown
                                        showMonthDropdown
                                        dropdownMode="select"
                                    />
                                    <ErrorMessage name="dateOfExpiry" component="span" className={styles.errorText} />
                                </div>

                                <div className={`${styles.fieldGroup} ${styles.fullWidth}`}>
                                    <label>
                                        Place of Issue <span className={styles.requiredStar}>*</span>
                                    </label>
                                    <Field
                                        type="text"
                                        name="placeOfIssue"
                                        placeholder="e.g. Passport Office, New York"
                                        className={errors.placeOfIssue && touched.placeOfIssue ? styles.inputError : ''}
                                    />
                                    <ErrorMessage name="placeOfIssue" component="span" className={styles.errorText} />
                                </div>
                            </div>

                            {/* Contact Details & Affiliation */}
                            <div className={styles.sectionHeader}>
                                <h3>Contact Details & Affiliation</h3>
                            </div>

                            <div className={styles.gridTwo}>
                                <div className={styles.fieldGroup}>
                                    <label>
                                        Email ID <span className={styles.requiredStar}>*</span> <span className={styles.hint}>(Official/Personal)</span>
                                    </label>
                                    <Field
                                        type="email"
                                        name="emailId"
                                        placeholder="jane.doe@example.com"
                                        className={errors.emailId && touched.emailId ? styles.inputError : ''}
                                    />
                                    <ErrorMessage name="emailId" component="span" className={styles.errorText} />
                                </div>

                                <div className={styles.fieldGroup}>
                                    <label>
                                        Mobile Number <span className={styles.requiredStar}>*</span>
                                    </label>
                                    <Field
                                        type="text"
                                        name="mobileNumber"
                                        placeholder="+1 234 567 8900"
                                        className={errors.mobileNumber && touched.mobileNumber ? styles.inputError : ''}
                                    />
                                    <ErrorMessage name="mobileNumber" component="span" className={styles.errorText} />
                                </div>

                                <div className={`${styles.fieldGroup} ${styles.fullWidth}`}>
                                    <label>
                                        Event Details / Affiliation <span className={styles.requiredStar}>*</span>
                                    </label>
                                    <Field
                                        as="textarea"
                                        name="eventDetails"
                                        rows="2"
                                        className={errors.eventDetails && touched.eventDetails ? styles.inputError : ''}
                                    />
                                    <ErrorMessage name="eventDetails" component="span" className={styles.errorText} />
                                </div>
                            </div>

                            <button type="submit" disabled={isSubmitting} className={styles.submitBtn}>
                                {isSubmitting ? (
                                    <span className={styles.spinnerContainer}>
                                        <span className={styles.spinner}></span> Submitting...
                                    </span>
                                ) : (
                                    'Submit Participant Information'
                                )}
                            </button>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default ParticipantInfoForm;
